"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  buildDefaultFieldValues,
  extractFieldsFromData,
  isValidId,
} from "../../form3/utils";
import { INITIAL_STATE } from "../constants";
import type {
  FormState,
  SemesterData,
  SubjectData,
  CategoryData,
  CategoryItemData,
} from "../types";

interface UseForm7DataArgs {
  selectedSection: any;
  pageComponentId?: string;
  variantConfig: any;
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  componentFieldsConfig: any[];
}

export function useForm7Data({
  selectedSection,
  pageComponentId,
  variantConfig,
  formState,
  setFormState,
  componentFieldsConfig,
}: UseForm7DataArgs) {
  const dispatch = useAppDispatch();
  const prevPageComponentIdRef = useRef<{ pageComponentId?: string; componentId?: string }>({});
  const prevSectionIdRef = useRef<string | undefined>(undefined);

  const reduxState = useAppSelector((state) =>
    variantConfig?.selector ? variantConfig.selector(state.page as any) : null
  ) as Record<string, any> | null;

  // ═══════════ APPLY DATA TO FORM ═══════════
  const applyDataToForm = useCallback(
    (data: Record<string, any>) => {
      variantConfig?.setAction && dispatch(variantConfig.setAction(data));
      const fields = extractFieldsFromData(data, componentFieldsConfig);

      const semesters: SemesterData[] = (data.semesters || []).map((sem: any) => ({
        ...sem,
        categories: (sem.categories || []).map((cat: any) => ({
          ...cat,
          items: cat.items || [],
        })),
      }));

      const subjects: SubjectData[] = (data.subjects || []).map((sub: any) => ({
        ...sub,
        electives: sub.electives || [],
      }));

      const allCategoryIds = new Set<number>();
      semesters.forEach((sem) => {
        (sem.categories || []).forEach((cat: CategoryData) => {
          allCategoryIds.add(cat.id);
        });
      });

      setFormState((prev) => ({
        ...prev,
        componentFields: fields,
        parentId: data.id,
        tools: data.tools || [],
        skills: data.skills || [],
        semesters,
        subjects,
        expandedSemesterIds: new Set(semesters.map((s: SemesterData) => s.id)),
        expandedCategoryIds: allCategoryIds,
        selectedSemesterFilter: semesters.length > 0 ? semesters[0].id : null,
      }));
    },
    [variantConfig, componentFieldsConfig, dispatch, setFormState]
  );

  // ═══════════ DATA LOADING ═══════════
  const loadAllData = useCallback(async () => {
    if (!variantConfig?.getMethod || !pageComponentId || !isValidId(pageComponentId)) {
      if (!pageComponentId) {
        const fields = buildDefaultFieldValues(componentFieldsConfig);
        setFormState((prev) => ({ ...prev, componentFields: fields, parentId: null }));
      }
      return;
    }

    try {
      const response = await variantConfig.getMethod(Number(pageComponentId));
      const data = response?.data || response || {};

      const isErrorResponse = data && typeof data.status === "number" && data.status >= 100 && data.message && !data.id;

      if (data && !isErrorResponse && Object.keys(data).length > 0 && data.id) {
        applyDataToForm(data);
      } else {
        const sectionData = selectedSection?.data;
        if (sectionData && sectionData.id) {
          applyDataToForm(sectionData);
        } else {
          const fields = buildDefaultFieldValues(componentFieldsConfig);
          setFormState((prev) => ({ ...prev, componentFields: fields, parentId: null, tools: [], skills: [], semesters: [], subjects: [] }));
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      const sectionData = selectedSection?.data;
      if (sectionData && sectionData.id) {
        applyDataToForm(sectionData);
      } else {
        const fields = buildDefaultFieldValues(componentFieldsConfig);
        setFormState((prev) => ({ ...prev, componentFields: fields, parentId: null }));
      }
    }
  }, [variantConfig, pageComponentId, componentFieldsConfig, selectedSection?.data, applyDataToForm, setFormState]);

  // Main effect - handles both reset and data loading
  useEffect(() => {
    const prevRef = prevPageComponentIdRef.current;
    const isInitialLoad = !prevRef.pageComponentId;
    const widgetChanged =
      prevRef.pageComponentId !== pageComponentId ||
      prevRef.componentId !== selectedSection?.componentId ||
      prevSectionIdRef.current !== selectedSection?.id;

    if (isInitialLoad || widgetChanged) {
      prevPageComponentIdRef.current = { pageComponentId, componentId: selectedSection?.componentId };
      prevSectionIdRef.current = selectedSection?.id;

      setFormState({ ...INITIAL_STATE, expandedSemesterIds: new Set(), expandedCategoryIds: new Set() });

      if (pageComponentId && isValidId(pageComponentId)) {
        loadAllData();
      }
    } else if (!reduxState?.id && pageComponentId && isValidId(pageComponentId)) {
      loadAllData();
    }
  }, [selectedSection?.id, selectedSection?.componentId, pageComponentId, reduxState?.id, loadAllData, setFormState]);

  // ═══════════ COMPUTED VALUES ═══════════
  const filteredSubjects = useMemo(() => {
    if (!formState.selectedSemesterFilter) return formState.subjects;
    return formState.subjects.filter((s) => s.semester_id === formState.selectedSemesterFilter);
  }, [formState.subjects, formState.selectedSemesterFilter]);

  const selectedSemesterCategories = useMemo(() => {
    const semId = formState.formFields.semester_id;
    if (!semId) return [];
    const sem = formState.semesters.find((s) => s.id === Number(semId));
    return sem?.categories || [];
  }, [formState.formFields.semester_id, formState.semesters]);

  const availableCategoryItems = useMemo(() => {
    if (formState.editingSection !== "subject" || !formState.formFields.category_id) return [];
    const cat = selectedSemesterCategories.find((c: CategoryData) => c.id === Number(formState.formFields.category_id));
    return cat?.items || [];
  }, [formState.editingSection, formState.formFields.category_id, selectedSemesterCategories]);

  return {
    reduxState,
    loadAllData,
    filteredSubjects,
    selectedSemesterCategories,
    availableCategoryItems,
  };
}
